/**
 * Ryan O'Dowd
 * 2021-06-28
 * © Copyright 2021 Oakwood Software Consulting, Inc. All Rights Reserved.
 */
import FormControl from '@components/common/FormControl';
import {
  TextInput as PaperTextInput,
} from 'react-native-paper';
import React from 'react';
import getStyles from './styles';
import globals from '@utils/globals';
import hooks from '@hooks';

const TextInput = React.forwardRef((props: TextInputProps & React.ComponentProps<typeof PaperTextInput>, ref) => {
  const styles = hooks.useStyles(getStyles);
  const {
    style,
    mode, // @NOTE: this is not passed to PaperTextInput
    label, // @NOTE: this is not passed to PaperTextInput
    contentStyle,
    ...restProps
  } = props;

  if (globals.deviceType !== 'mobile') {
    // on desktop, autocorrect makes typing virtually impossible due to corrections made mid-word
    restProps.autoCorrect = false;
  }

  if (mode === 'outlined') {
    restProps.textColor = styles.isDarkMode ? styles.colors.white : styles.colors.primaryExtraDark;
    restProps.underlineColor = '#00000000';
    restProps.activeUnderlineColor = '#00000000';
  }

  const innerJsx = (
    <PaperTextInput
      ref={ref as any}
      keyboardAppearance={styles.isDarkMode ? 'dark' : 'light'}
      dense={true}
      theme={{
        colors: {
          text: props.textColor || styles.colors.white, // @TODO: this shouldn't be necessary since textColor is a valid prop
        },
      }}
      placeholderTextColor={styles.isDarkMode ? styles.colors.gray : styles.colors.lightGray}
      cursorColor={styles.colors.accent}// @NOTE: android only, but it's required because selectionColor is selction + cursor, but only on iOS
      selectionColor={styles.colors.accent}
      allowFontScaling={false}
      {...restProps}
      style={[styles.container, style]}
      contentStyle={[styles.textStyles, contentStyle]}
    />
  );

  if (props.noFormControl) {
    return innerJsx;
  }

  return (
    <FormControl
      mode={props.mode}
      label={label}
      disabled={props.disabled}
      isAlert={props.isAlert}
    >
      {innerJsx}
    </FormControl>
  );
});

class TextInputProps {
  label?: string;
  noFormControl?: boolean;
  isAlert?: boolean;
};

TextInput.displayName = 'TextInput';
export default TextInput;
